//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { RunAudit, RunKind, RunOutcome, ScheduleChangeAudit } from "../../api/cd-robot/v1/cd_robot";
import * as googleTypes from "../../api/googleTypes";

export const CD_ROBOT_EVENT_TYPES = ["internal-cd-robot.run.finished", "internal-cd-robot.schedule.updated"];

export const hasRunAudit = (run?: RunAudit): boolean => !!run?.run_id;

export const normalizeRunKind = (runKind?: RunKind | string | null): RunKind | undefined => {
  if (runKind == null || runKind === "") {
    return undefined;
  }
  if (typeof runKind === "number") {
    return runKind;
  }
  const fromName = RunKind[runKind as keyof typeof RunKind];
  if (typeof fromName === "number") {
    return fromName;
  }
  const numeric = Number(runKind);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return undefined;
};

export const normalizeRunOutcome = (outcome?: RunOutcome | string | null): RunOutcome | undefined => {
  if (outcome == null || outcome === "") {
    return undefined;
  }
  if (typeof outcome === "number") {
    return outcome;
  }
  const fromName = RunOutcome[outcome as keyof typeof RunOutcome];
  if (typeof fromName === "number") {
    return fromName;
  }
  const numeric = Number(outcome);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return undefined;
};

export const runKindLabel = (runKind?: RunKind | string): string => {
  switch (normalizeRunKind(runKind)) {
    case RunKind.RUN_KIND_SCHEDULED:
      return "Scheduled";
    case RunKind.RUN_KIND_MANUAL:
      return "Manual";
    default:
      return "Unknown";
  }
};

export const runOutcomeLabel = (outcome?: RunOutcome | string): string => {
  switch (normalizeRunOutcome(outcome)) {
    case RunOutcome.RUN_OUTCOME_SUCCESS:
      return "Success";
    case RunOutcome.RUN_OUTCOME_FAILED:
      return "Failed";
    case RunOutcome.RUN_OUTCOME_ABORTED:
      return "Aborted";
    default:
      return "In progress";
  }
};

export const runOutcomeColor = (outcome?: RunOutcome | string): "green" | "red" | "orange" | "grey" => {
  switch (normalizeRunOutcome(outcome)) {
    case RunOutcome.RUN_OUTCOME_SUCCESS:
      return "green";
    case RunOutcome.RUN_OUTCOME_FAILED:
      return "red";
    case RunOutcome.RUN_OUTCOME_ABORTED:
      return "orange";
    default:
      return "grey";
  }
};

export const actorLabel = (id?: string, email?: string): string => {
  if (email) {
    return email;
  }
  if (id) {
    return id;
  }
  return "—";
};

export const timestampToIso = (ts?: googleTypes.Timestamp | string): string | undefined => {
  if (!ts) {
    return undefined;
  }
  if (typeof ts === "string") {
    return ts;
  }
  const time = ts.getTime();
  if (!Number.isFinite(time)) {
    return undefined;
  }
  return new Date(time).toISOString();
};

export const hasScheduleChangeAudit = (change?: ScheduleChangeAudit): boolean =>
  !!timestampToIso(change?.changed_at) || !!change?.changed_by_email || !!change?.changed_by_id || !!change?.previous_schedule || !!change?.new_schedule;

export const formatDuration = (startedAt?: googleTypes.Timestamp, endedAt?: googleTypes.Timestamp): string | undefined => {
  const startIso = timestampToIso(startedAt);
  const endIso = timestampToIso(endedAt);
  if (!startIso) {
    return undefined;
  }
  const start = Date.parse(startIso);
  const end = endIso ? Date.parse(endIso) : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return undefined;
  }
  const totalMinutes = Math.floor((end - start) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};
